'use no memo';

/* eslint-disable complexity */
import { some } from 'lodash-es';
import { useContext } from 'react';

import { isCateringOrderFlow, isMarketplacePlatform } from '@local/utils';
import { Restaurant } from '@local/api';

import { ModalContext } from 'context/ModalContext';
import { removeItemsOrderById } from 'context/OrderContext/actions';
import { OrderContext } from 'context/OrderContext';
import { RestaurantContext } from 'context/RestaurantContext';
import { UserContext } from 'context/UserContext';
import { useSelectedAddressStore } from 'store';

import {
  ANALYTICS_EVENT_NAME,
  MAX_ORDER_CLOSURE_DESCRIPTION,
  MENU_EXPIRED_MODAL_COPY,
} from 'helpers/constants';
import {
  ANALYTICS_EVENT_TYPES,
  logAnalyticsEvent,
  logException,
  logMessage,
} from 'helpers/loggers';
import { getPaymentTypeForAnalytics } from 'helpers/analytics';
import { MODAL_TYPE } from 'helpers/modals';
import {
  ADDRESS_OUTSIDE_DELIVERY_AREAS,
  BETTER_DEAL_APPLIED,
  CATERING_NOT_ENABLED,
  CITY_ERROR_FIELD,
  CURBSIDE_CLOSED,
  CURBSIDE_INVALID_TABLET_VERSION,
  CURBSIDE_INVALID_VEHICLE_COLOR,
  CURBSIDE_INVALID_VEHICLE_TYPE,
  CURBSIDE_MISSING_VEHICLE,
  CURBSIDE_MISSING_VEHICLE_COLOR,
  CURBSIDE_MISSING_VEHICLE_TYPE,
  CURBSIDE_UNAVAILABLE,
  CVV_REQUIRED,
  DELIVERY_UNAVAILABLE,
  EXPECTED_ORDER_VALIDATION_ERRORS,
  DINE_IN_TABLE_NOT_ALLOWED,
  DINE_IN_TABLE_REQUIRED,
  INVALID_MISSING_PHONE_NUMBER,
  INVALID_PROMO_CODE,
  ITEM_EXPIRED,
  JSON_REQUIRED_FIELD_MISSING,
  LENGTH_BELOW_MIN,
  MENU_EXPIRED,
  MENU_ID_ERROR_FIELD,
  MOD_CATEGORY_EXPIRED,
  MODS_INVALID_FOR_ITEM,
  MOD_EXPIRED,
  ORDER_LEAD_TIME_EXPIRED,
  PICKUP_UNAVAILABLE,
  PREPTIME_CHANGED,
  PREPTIME_REMOVED,
  RESTAURANT_DOES_NOT_ALLOW_SINGLE_USE,
  RESTAURANT_DOES_NOT_ALLOW_TIP,
  RESTAURANT_NOT_LIVE,
  STREET_ERROR_FIELD,
  TIMESLOT_ORDER_THROTTLED,
  TIP_AND_TIP_PERCENTAGE_EXCLUSIVE,
  TIP_TOO_BIG,
  UNAUTHORIZED,
  genericDeclineMsg,
  genericDeclineTitle,
  getIs5xxError,
} from 'helpers/api';
import { isOnPage } from 'helpers/url';
import { FULFILLMENT_METHODS } from 'helpers/order';
import { getIsDirectToMpRedirect } from 'helpers/configureRedirects';

import useEffectEvent from 'hooks/useEffectEvent';

import { ApiError } from 'types/api';
import {
  FulfillMethod,
  OrderDetailResponseV6,
  OrderValidateResponseV6,
} from 'types/order';
import { OnAddItemType } from 'types/menu';

function sendPurchaseFailureEvent({
  errorMessage,
  errorCode,
  selectedCard,
  isEmbedded,
  restaurant,
  orderTotal,
}: {
  errorMessage: string;
  errorCode: number;
  selectedCard: React.ContextType<typeof UserContext>['selectedCard'];
  isEmbedded: boolean | undefined;
  restaurant: Restaurant;
  orderTotal: number | undefined;
}) {
  const isCashOnly = restaurant?.is_post_pay;
  logAnalyticsEvent({
    eventName: ANALYTICS_EVENT_NAME.purchaseFailure,
    eventType: ANALYTICS_EVENT_TYPES.Transaction,
    attributes: {
      error_reason: errorMessage,
      error_code: errorCode,
      payment_type: getPaymentTypeForAnalytics({
        selectedCard,
        isCashOnly,
        orderTotal,
      }),
      is_embedded_site: isEmbedded,
      is_direct_to_marketplace_redirect: getIsDirectToMpRedirect(),
      restaurant_location_id: restaurant?.id,
    },
  });
  // Only genuine 5xx faults are errors; expected validation codes are silent.
  // Unrecognized non-5xx codes log a warning so they stay visible without paging.
  if (getIs5xxError(errorCode)) {
    logException({
      name: `Purchase Failure: ${errorMessage}`,
      message: `Error Code: ${errorCode}`,
    });
  } else if (!EXPECTED_ORDER_VALIDATION_ERRORS.includes(String(errorCode))) {
    logMessage(
      `Unhandled order validation: ${errorMessage}`,
      { errorCode },
      'warning'
    );
  }
}

export default function useHandleInvalidOrder() {
  const { openModal } = useContext(ModalContext);
  const { order, resetOrder, updateOrder } = useContext(OrderContext);
  const { getNextAvailableTime, handleFetchRestaurant, restaurant } =
    useContext(RestaurantContext);
  const { selectedCard } = useContext(UserContext);
  const selectedAddress = useSelectedAddressStore((store) => store.address);
  const setSelectedAddress = useSelectedAddressStore(
    (store) => store.setAddress
  );

  /* this won't necessarily be used in a useEffect however this itself
   * should never retrigger any effects does happen to be used in so use a useEffectEvent
   * anyways for DX so we never have to worry about this being an issue */
  return useEffectEvent(
    async (
      type: 'submit' | 'validate',
      responseData: OrderDetailResponseV6 | OrderValidateResponseV6 | undefined,
      orderErrors: ApiError[]
    ) => {
      if (!restaurant || !order) {
        // not really a valid state but making TypeScript happy
        throw new Error(
          'invalid state: restaurant or order undefined in handleInvalidOrder'
        );
      }

      const errorMessage = orderErrors[0]?.message;
      const errorCode = orderErrors.length && parseInt(orderErrors[0].code, 10);
      const is5xxError = getIs5xxError(errorCode);

      if (type === 'submit') {
        // only send atttribute when not on MP
        const isEmbedded = isMarketplacePlatform()
          ? undefined
          : window.top !== window;

        sendPurchaseFailureEvent({
          errorCode,
          errorMessage,
          isEmbedded,
          restaurant,
          selectedCard,
          orderTotal: order.total_due,
        });

        const lastSubmittedAddressId = (
          responseData as OrderDetailResponseV6 | undefined
        )?.customer?.delivery?.addresses?.find(
          (address) => address.is_last_submitted
        )?.id;

        if (lastSubmittedAddressId && selectedAddress) {
          setSelectedAddress({
            ...selectedAddress,
            id: lastSubmittedAddressId,
          });
        }
      }

      if (
        some(orderErrors, { code: PICKUP_UNAVAILABLE }) ||
        some(orderErrors, { code: DELIVERY_UNAVAILABLE }) ||
        some(orderErrors, { code: CURBSIDE_CLOSED }) ||
        some(orderErrors, { code: CURBSIDE_UNAVAILABLE }) ||
        some(orderErrors, { code: CURBSIDE_INVALID_TABLET_VERSION }) ||
        some(orderErrors, { code: ORDER_LEAD_TIME_EXPIRED }) ||
        (some(orderErrors, { code: JSON_REQUIRED_FIELD_MISSING }) &&
          some(orderErrors, { field: MENU_ID_ERROR_FIELD }))
      ) {
        // Update error modals with latest details
        const updatedRestaurant = await handleFetchRestaurant(restaurant.id);

        if (!updatedRestaurant) {
          return;
        }

        // CN-10963 need to reset order if temp closed by OrderBot
        if (
          updatedRestaurant.closures?.length &&
          updatedRestaurant.closures.find(
            (closure) => closure.description === MAX_ORDER_CLOSURE_DESCRIPTION
          )
        ) {
          resetOrder();
          return;
        }

        const isAheadAvailable =
          updatedRestaurant.order_ahead?.[
            `is_${order.fulfill_method}_ahead_available`
          ];

        if (isAheadAvailable) {
          openModal(MODAL_TYPE.timeUnavailable, { noCloseOverlay: true });
        } else {
          const nextAvailable = getNextAvailableTime();

          openModal(MODAL_TYPE.dialog, {
            ...MENU_EXPIRED_MODAL_COPY,
            noCloseOverlay: true,
            isAlert: true,
            isError: true,
            onConfirm: () => {
              window.location.reload();
            },
          });

          updateOrder({
            fulfill_method: nextAvailable.fulfillMethod as FulfillMethod,
            when: nextAvailable.nextAvailableTime,
          });
        }
      } else if (some(orderErrors, { code: TIMESLOT_ORDER_THROTTLED })) {
        // Update restaurant to get valid time slots
        void handleFetchRestaurant(restaurant.id);

        const error = orderErrors.filter(
          (orderError) => orderError.code === TIMESLOT_ORDER_THROTTLED
        );
        const isDeliveryAheadAvailable =
          !!restaurant.order_ahead?.is_delivery_ahead_available;
        // FIXME: see CN-19150 - This is a temp fix for order throttling until BE work is complete
        const isDeliveryAndDeliveryAheadUnavailable =
          !isDeliveryAheadAvailable &&
          order.fulfill_method === FULFILLMENT_METHODS.delivery;
        // FIXME: (CN-19150) checks next avail time for the forced pickup fulfillment if delivery ahead not avail
        const nextAvailable = getNextAvailableTime();

        // yield put(updateOrderThrottlingMeta());
        updateOrder({
          // FIXME: (CN-19150) force user to use pickup as fulfillment method if delivery ahead unavail
          fulfill_method: isDeliveryAndDeliveryAheadUnavailable
            ? FULFILLMENT_METHODS.pickup
            : order.fulfill_method,
          when: isDeliveryAndDeliveryAheadUnavailable
            ? nextAvailable.nextAvailableTime
            : (error[0].next_available_time as string),
        });

        if (!order.when && !error[0].next_available_time) {
          openModal(MODAL_TYPE.dialog, {
            title: "We're a little busy right now.",
            confirmLabel: 'Okay',
            noCloseOverlay: true,
            isAlert: true,
            isError: true,
          });
        } else {
          openModal(MODAL_TYPE.orderingUnavailable, {
            isDeliveryAndDeliveryAheadUnavailable,
            isOrderThrottleError: true,
            nextAvailableWhen: error[0].next_available_time,
            when: order.when,
            noCloseOverlay: true,
          });
        }
      } else if (
        some(orderErrors, { code: ADDRESS_OUTSIDE_DELIVERY_AREAS }) ||
        (some(orderErrors, { code: JSON_REQUIRED_FIELD_MISSING }) &&
          (some(orderErrors, { field: CITY_ERROR_FIELD }) ||
            some(orderErrors, { field: STREET_ERROR_FIELD })))
      ) {
        openModal(MODAL_TYPE.fulfillmentDetails, {
          fulfillmentMethod: order.fulfill_method,
          noCloseOverlay: !!restaurant.delivery_only,
          shouldDefaultToPickup: !restaurant.delivery_only,
        });
      } else if (
        some(orderErrors, { code: CURBSIDE_INVALID_VEHICLE_COLOR }) ||
        some(orderErrors, { code: CURBSIDE_INVALID_VEHICLE_TYPE }) ||
        some(orderErrors, { code: CURBSIDE_MISSING_VEHICLE }) ||
        some(orderErrors, { code: CURBSIDE_MISSING_VEHICLE_COLOR }) ||
        some(orderErrors, { code: CURBSIDE_MISSING_VEHICLE_TYPE })
      ) {
        // reset vehicle info on any of these errors
        updateOrder({ vehicle: undefined });

        openModal(MODAL_TYPE.fulfillmentDetails, {
          fulfillmentMethod: FULFILLMENT_METHODS.curbside,
        });
      } else if (
        some(orderErrors, { code: ITEM_EXPIRED }) ||
        some(orderErrors, { code: MOD_CATEGORY_EXPIRED }) ||
        some(orderErrors, { code: MOD_EXPIRED }) ||
        some(orderErrors, { code: MODS_INVALID_FOR_ITEM })
      ) {
        // start ITEM_EXPIRED
        // get array of item ids
        const badItems =
          orderErrors
            .filter((error) => error.code === '34002' || error.code === '34005')
            .map((error) => String(error.value)) || [];
        // get item data
        const rejectedItems = order.items.filter(
          (item) =>
            (item.sizeId && badItems.includes(item.sizeId)) ||
            badItems.includes(item.id)
        );
        // end ITEM_EXPIRED

        // start MOD_EXPIRED
        // array of mod ids
        const badMods =
          orderErrors
            .filter((error) => error.code === MOD_EXPIRED)
            .map((error) => String(error.value)) || [];
        // get full item data
        const rejectedModItems: OnAddItemType[] = [];
        order.items.forEach((item) => {
          item.modifierCategories.forEach((modCat) => {
            badMods.forEach((badMod) => {
              if (
                modCat.modifiers.some((m) => m.id === badMod) &&
                !rejectedItems.some(
                  (rejectedItem) => rejectedItem.id === item.id
                ) // dont add again if item is already in rejected items
              )
                rejectedModItems.push(item);
            });
          });
        });
        // get array of parent ids for bad mods
        const badModItems = rejectedModItems.map((item) => item.id);
        // end MOD_EXPIRED

        // start MOD_CATEGORY_EXPIRED
        const badModCategories =
          orderErrors
            .filter((error) => error.code === MOD_CATEGORY_EXPIRED)
            .map((error) => String(error.value)) || [];
        const rejectedModCatItems: OnAddItemType[] = [];
        order.items.forEach((item) => {
          item.modifierCategories.forEach((modCat) => {
            if (
              badModCategories.includes(modCat.id) &&
              !rejectedItems.some(
                (rejectedItem) => rejectedItem.id === item.id
              ) &&
              !rejectedModItems.some(
                (rejectedItem) => rejectedItem.id === item.id
              )
            )
              rejectedModCatItems.push(item);
          });
        });
        const badModCatItemIds = rejectedModCatItems.map((item) => item.id);
        // end MOD_CATEGORY_EXPIRED

        openModal(MODAL_TYPE.itemExpired, {
          rejectedItems: [
            ...rejectedItems,
            ...rejectedModItems,
            ...rejectedModCatItems,
          ],
        });

        updateOrder(
          removeItemsOrderById(
            [...badItems, ...badModItems, ...badModCatItemIds],
            false
          )
        );
      } else if (some(orderErrors, { code: MENU_EXPIRED })) {
        openModal(MODAL_TYPE.dialog, {
          ...MENU_EXPIRED_MODAL_COPY,
          noCloseOverlay: true,
          isAlert: true,
          isError: true,
          onConfirm: () => {
            window.location.reload();
          },
        });
      } else if (
        some(orderErrors, { code: INVALID_PROMO_CODE }) ||
        some(orderErrors, { code: BETTER_DEAL_APPLIED })
      ) {
        // If promo errors, clear out promo code
        updateOrder({ promo_code: null }, false);
      } else if (
        some(orderErrors, { code: TIP_TOO_BIG }) ||
        some(orderErrors, { code: TIP_AND_TIP_PERCENTAGE_EXCLUSIVE })
      ) {
        openModal(MODAL_TYPE.dialog, {
          title: "Let's Take a Second Look.",
          message: errorMessage,
          confirmLabel: 'Go Back',
          noCloseOverlay: true,
          isAlert: true,
          isError: true,
        });
      } else if (
        some(orderErrors, { code: DINE_IN_TABLE_NOT_ALLOWED }) ||
        some(orderErrors, { code: DINE_IN_TABLE_REQUIRED })
      ) {
        openModal(MODAL_TYPE.dialog, {
          message: errorMessage,
          onConfirm: () => {
            window.location.reload();
          },
          noCloseOverlay: true,
          isAlert: true,
        });

        if (some(orderErrors, { code: DINE_IN_TABLE_NOT_ALLOWED })) {
          updateOrder({ table_name: null });
        }
      } else if (some(orderErrors, { code: UNAUTHORIZED })) {
        logException({
          name: 'Lost session on submit order',
          message: `err: ${orderErrors[0].message}`,
        });
      } else if (some(orderErrors, { code: CVV_REQUIRED })) {
        openModal(MODAL_TYPE.dialog, {
          message: genericDeclineMsg,
          title: genericDeclineTitle,
          confirmLabel: 'Back to Checkout',
          isAlert: true,
        });
      } else if (some(orderErrors, { code: RESTAURANT_NOT_LIVE })) {
        openModal(MODAL_TYPE.dialog, {
          message: errorMessage,
          isAlert: true,
          onConfirm: () => {
            if (isOnPage('checkout')) {
              window.location.reload();
            }
          },
        });

        resetOrder();
      } else if (some(orderErrors, { code: CATERING_NOT_ENABLED })) {
        openModal(MODAL_TYPE.dialog, {
          message: errorMessage,
          confirmLabel: 'Go to Ordering',
          isAlert: true,
          noCloseOverlay: true,
          onConfirm: () => {
            if (isCateringOrderFlow()) {
              window.location.href = window.location.href.replace(
                '/catering/',
                '/order/'
              );
            }
          },
        });
      } else if (some(orderErrors, { code: PREPTIME_CHANGED })) {
        openModal(MODAL_TYPE.dialog, {
          title: 'New Ready Time',
          message: errorMessage,
          confirmLabel: 'Okay',
          isAlert: true,
          onConfirm: () => {
            window.location.reload();
          },
          noCloseOverlay: true,
        });
      } else if (some(orderErrors, { code: PREPTIME_REMOVED })) {
        openModal(MODAL_TYPE.dialog, {
          title: 'Order Update',
          message: errorMessage,
          confirmLabel: 'Okay',
          isAlert: true,
          onConfirm: () => {
            window.location.reload();
          },
          noCloseOverlay: true,
        });
      } else if (
        some(orderErrors, { code: INVALID_MISSING_PHONE_NUMBER }) ||
        some(orderErrors, { code: LENGTH_BELOW_MIN })
      ) {
        openModal(MODAL_TYPE.confirmAccount, {
          noCloseOverlay: true,
        });
      } else if (type === 'submit' && !is5xxError) {
        openModal(MODAL_TYPE.dialog, {
          message: errorMessage
            ? `${errorMessage}`
            : 'Sorry about that. Go back and try again.',
          isAlert: true,
          isError: true,
          noCloseOverlay: true,
          onConfirm: () => {
            window.location.reload();
          },
        });
      } else if (some(orderErrors, { code: RESTAURANT_DOES_NOT_ALLOW_TIP })) {
        updateOrder({
          tips: {
            restaurant_tip: null,
            managed_delivery_tip: null,
          },
        });
      } else if (
        some(orderErrors, { code: RESTAURANT_DOES_NOT_ALLOW_SINGLE_USE })
      ) {
        updateOrder({
          include_single_use_items: false,
        });

        void handleFetchRestaurant(restaurant.id);
      }
    }
  );
}
